import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import { TableHeader, ProcessingStatusList, ColumnMenu } from '@/shared/type';
import vueRouter from '@/router/index';                                                    // ルート情報取得
import SystemConst from '@/shared/system-const';                                           // 定数定義
import CommonFunction from '@/shared/common-function';                                     // 共通関数定義
import MessageConst from '@/shared/message-const';                                         // 共通メッセージ定義
import HttpStatus from 'http-status-codes';                                                // HTTPステータスコード取得用
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue'; // 共通メッセージダイアログコンポーネント
import { DataOptions } from 'vuetify';                                                     // v-data-tableのupdateイベントの引数を利用するための型
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';     // フィルタ用共通コンポーネント
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    processingStatusList: ProcessingStatusList[];
    processStatusListDownloadDialog: ColumnMenu;
    processStatusDeleteDialog: ColumnMenu;
    visibleColumnMenu: ColumnMenu;
    totalCount: number;                                                    // 処理状況の総件数
    isLoading: boolean;
    isDownLoading: boolean;
    displayConfirmationDialog: boolean;                                    // Excel出力確認ダイアログ表示フラグ
    filterDefs: TableFilterDefs;                                           // フィルタ定義
    pageOptions: DataOptions;                                              // ページ情報・ソート情報
    filters: TableFilters;                                                 // フィルタ情報
    isFromDetail: boolean;                                                 // 詳細画面からの遷移フラグ
};

/**
 * 処理状況コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonTableFilter },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.PROCESSING_STATUS_LIST;// テーブル高さ調整用変数
        const router = vueRouter;                                           // 画面遷移用
        const tableHeaderClass: string[] = ['secondary subtitle-2'];        // ヘッダ色
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;               // 1ページあたりの行数
        const commonRef = ref<any>(null);                                   // メッセージダイアログ
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;            // テーブルのデータ取得中に表示するテキスト
        const page = ref<number>(1);                                        // ページ情報（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[1]);                  // 1ページあたりの行数（状態管理用）
        const sortBy = ref<string[]>([]);                                   // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                // ソート昇順、降順（状態管理用）
        const child = ref<any>();                                           // テーブルフィルタ一覧
        let isPageLastData = false;                                         // ページの最終データフラグ
        const state: reactiveData = reactive<reactiveData>({                // リアクティブな変数の宣言
            windowSize: { x: 0, y: 0 },
            processingStatusList: [],                                       // 処理状況の一覧
            processStatusListDownloadDialog: {},                            // 処理データのダウンロードダイアログの表示有無
            processStatusDeleteDialog: {},                                  // 処理データの削除ダイアログの表示有無
            visibleColumnMenu: {},                                          // 列メニューの表示有無
            totalCount: 0,                                                  // 処理状況の総件数
            isLoading: false,                                               // テーブルがロード中: true ロード中でない: false
            isDownLoading: false,                                           // ファイルがダウンロード中: true ダウンロード中でない: false
            displayConfirmationDialog: true,                                // Excel出力確認ダイアログ表示フラグ
            filterDefs: {                                                   // フィルタ定義
                'coverage': { title: '対象範囲', types: 'CheckBox' },
                'targetUser': { title: '対象ユーザー', types: 'KeyWordPartialMatch' },
                'executionUser': { title: '実行者', types: 'KeyWordPartialMatch' },
                'executionAt': { title: '実行日時', types: 'DatePeriod' },
                'processingStatus': { title: '処理状況', types: 'CheckBox' }
            },
            pageOptions: {                                                  // ページ情報・ソート情報
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filters: {},                                                    // フィルタ情報
            isFromDetail: false                                             // 詳細画面からの遷移フラグ
        });
        const processingStatusTableHeaders: TableHeader[] = [               // 処理状況ヘッダ情報(テーブルのヘッダー)
            // 対象範囲
            {
                text: '対象範囲',
                value: 'coverage',
                width: '100px',
                class: tableHeaderClass
            },
            // 対象ユーザー
            {
                text: '対象ユーザー',
                value: 'targetUser',
                width: '180px',
                class: tableHeaderClass
            },
            // 実行者
            {
                text: '実行者',
                value: 'executionUser',
                width: '180px',
                class: tableHeaderClass
            },
            // 実行日時
            {
                text: '実行日時',
                value: 'executionAt',
                width: '150px',
                class: tableHeaderClass
            },
            // 処理状況
            {
                text: '処理状況',
                value: 'processingStatus',
                width: '150px',
                class: tableHeaderClass
            },
            // 行メニュー
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];

        // フィルタのバリデーション
        // 対象ユーザー
        const targetUserRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '対象ユーザー')
        ];

        // 実行者
        const executionUserRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '実行者')
        ];

        // フィルターに設定された最大桁数
        const maxLength = SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH;

        created();
        /**
         * 初期表示
         */
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-list-status');

            // タイトル表示
            ctx.root.$store.commit('setMainTitle', '処理状況');

            // 処理状況一覧情報をクリア
            state.processingStatusList = [];

            // storeからフィルター情報を復元する
            state.filters = ctx.root.$store.state.processingStatusUsers.filters;

            // storeからページ情報を復元する
            page.value = ctx.root.$store.state.processingStatusUsers.page;
            itemsPerPage.value = ctx.root.$store.state.processingStatusUsers.itemsPerPage;

            // storeからソート情報を復元する
            sortBy.value = ctx.root.$store.state.processingStatusUsers.sortBy;
            sortDesc.value = ctx.root.$store.state.processingStatusUsers.sortDesc;

            // storeから詳細画面からの遷移フラグを復元する
            state.isFromDetail = ctx.root.$store.state.processingStatusUsers.isFromDetail;

            // フィルタの初期設定を行う
            // 初期設定が完了するまでawaitで待つ
            await initFilter();

            // vuexにフィルタ情報が保持されているデータを復元する
            const filterIdList = [];
            const filterParameters = [];
            for (const key in state.filters) {
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    if (val.isEnabled) {
                        // 有効なフィルタのIDを保存する
                        filterIdList.push(key);
                        // 有効なフィルタ条件を保存する
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元されたフィルタ情報がある場合
            if (filterIdList.length !== 0) {
                // 子コンポーネントのsetAllFiltersを呼び出し、フィルターを表示する(フィルタ更新処理から一覧が取得できる)
                child.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // 処理状況一覧情報取得
                updateList();
            }

            // 詳細画面からの遷移フラグにfalseを設定する
            state.isFromDetail = false;
        }

        /**
         * フィルタ初期設定
         * 
         * フィルタにDBから取得する必要のある条件がある場合、初期表示で取得する。
         */
        async function initFilter() {
            // フィルタ条件の内容を取得
            // ユーザー処理状況管理ストアのgetFilterConditionsを呼び出す
            const response = await ctx.root.$store.dispatch('processingStatusUsers/getFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // フィルタ（チェックボックス）に表示するデータを設定する
            // 対象範囲
            state.filterDefs['coverage'].chkItems = response.data.coverage;
            // 処理状況
            state.filterDefs['processingStatus'].chkItems = response.data.processingStatus;

            // バリデーションを設定する
            // 対象ユーザー
            state.filterDefs['targetUser'].validator = targetUserRules;
            // 実行者
            state.filterDefs['executionUser'].validator = executionUserRules;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // 詳細画面からの遷移以外の場合
            if (!state.isFromDetail) {
                // 1ページへ戻す
                state.pageOptions.page = 1;
                page.value = 1;
            }

            // 処理状況一覧を更新する
            updateList();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            // optionsの中身を比較して、いずれも変更されていなかった場合、
            // ページの更新を行わない
            if (page.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                return;
            }

            // ページ情報を設定する
            page.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソート情報を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 処理状況一覧を更新する
            updateList();
        }

        /**
         * 処理状況一覧の表示更新
         */
        async function updateList() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // ストアにパラメータを格納
            ctx.root.$store.commit('processingStatusUsers/setPage', page.value);
            ctx.root.$store.commit('processingStatusUsers/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('processingStatusUsers/setFilters', state.filters);
            ctx.root.$store.commit('processingStatusUsers/setSortBy', sortBy.value);
            ctx.root.$store.commit('processingStatusUsers/setSortDesc', sortDesc.value);

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 処理状況一覧取得APIでデータを取得する
            // GET /api/processing-status-users
            // パラメータ：作成したパラメータ
            const response = await ctx.root.$store.dispatch('processingStatusUsers/getProcesses',
                {
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 処理状況一覧のデータをstate.processingStatusListへ設定する
            state.processingStatusList = response.data.processingStatusUsers;

            // 総検索件数をstate.totalCountへ設定する
            state.totalCount = response.data.totalCount;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.processingStatusList.length === 1 && state.totalCount > 1) {
                isPageLastData = true;
            }

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 処理状況詳細画面へ遷移する
         * 
         * @param processId 中間処理ID
         */
        function navigationDetail(processId: string) {
            // 処理状況詳細画面へ中間処理IDを渡して遷移する。
            router.push(`/processing-status-users/detail/${processId}`);
        }

        /**
         * 処理状況EXCEL出力処理
         * 
         * @param processId 中間処理ID
         */
        async function outputExcel(processId: string) {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ファイルダウンロード中表示するダイアログをtrueにする
            state.isDownLoading = true;

            // パラメータを設定する
            const params = {
                processId: processId,                                                 // 中間処理ID
                fileName: SystemConst.EXCEL_API_PARAM.PROCESSING_STATUS_USERS_FILE    // Excelファイル名
            };

            // 処理状況ストアファイルを呼び出し、APIでデータを取得する
            const response = await ctx.root.$store.dispatch('processingStatusUsers/outputExcel', params);

            // ファイルダウンロード中表示するダイアログをfalseにする
            state.isDownLoading = false;

            // EXCEL出力ダイアログをfalseにする
            state.processStatusListDownloadDialog[processId] = false;

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }
        }

        /**
         * 処理状況削除処理
         * 
         * @param processId 中間処理ID
         */
        async function deleteProcess(processId: string) {
            // 処理中の処理状況確認APIを呼ぶ
            //   GET /api/processing-status-users/{processId}/check
            const checkResponse = await ctx.root.$store.dispatch('processingStatusUsers/getRunningStatus', { 'processId': processId });

            // API通信が正常か
            if (checkResponse.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(checkResponse);
                return;
            }

            // 処理中であれば削除出来ないため、ダイアログを表示して処理を終了する。
            if (checkResponse.data.count >= 1) {
                const displayMessage = CommonFunction.replaceArray(MessageConst.E_BECAUSE_OF, ['処理中', '削除']);
                commonRef.value.simpleDisplayMessageDialog(displayMessage);
                return;
            }

            // 処理状況削除APIを呼ぶ
            //   DELETE /api/processing-status-users/:processId
            const processResponse = await ctx.root.$store.dispatch('processingStatusUsers/deleteProcess', { 'processId': processId });

            // API通信が正常か
            if (processResponse.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(processResponse);
                return;
            }

            // 削除完了のダイアログを表示する
            commonRef.value.displayMessageDialog(processResponse);

            // ページの最終データフラグがtrueの場合、ページ情報を-1する
            if (isPageLastData) {
                state.pageOptions.page -= 1;
                page.value -= 1;
            }

            // 処理状況一覧を表示更新する
            updateList();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize() {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            tableHeight,
            state,
            processingStatusTableHeaders,
            page,
            linesPerPage,
            itemsPerPage,
            sortBy,
            sortDesc,
            child,
            commonRef,
            tableLoadingText,
            created,
            updateFilter,
            updateOptions,
            onResize,
            navigationDetail,
            outputExcel,
            deleteProcess
        };
    }
});
